import * as React from "react";
import { useState, useRef, useEffect } from "react";
import { styled } from "@mui/material/styles";
import { FormHelperText } from "@mui/material";
import { CommonInputProps, InputHelperText, Labeled } from "react-admin";
import { useInput, useResourceContext } from "ra-core";
import { Editor } from "@tinymce/tinymce-react";
import { ImageContentInput } from "./ImageContentInput";
import { ComponentInput } from "./ComponentInput";

export const TinyMCEContentInput = (props: TinyMCEContentInputProps) => {
  const {
    className,
    source,
    defaultValue,
    disabled,
    fullWidth,
    helperText,
    label,
    readOnly,
    required,
    base64,
    validate,
    ...rest
  } = props;

  const resource = useResourceContext(props);

  const {
    id,
    field,
    fieldState,
    formState: { isSubmitted },
  } = useInput({ ...rest, source, defaultValue, validate });

  const { error, invalid, isTouched } = fieldState;
  const [value, setValue] = useState(field.value);
  const [selectedElement, setSelectedElement] = useState(null);
  const editorRef = useRef(null);
  const imageContentInputRef = useRef(null);
  const componentInputRef = useRef(null);
  const componentEditRef = useRef(null);

  const setVars = (value) => {
    value = value
      .replaceAll(process.env.REACT_APP_AWS_CLOUDFRONT_URL, "%cloudFrontUrl%")
      .replaceAll(process.env.REACT_APP_BASE_URL, "%baseUrl%");
    return value;
  };

  const unsetVars = (value) => {
    return value
      .replaceAll("%cloudFrontUrl%", process.env.REACT_APP_AWS_CLOUDFRONT_URL)
      .replaceAll("%baseUrl%", process.env.REACT_APP_BASE_URL);
  };

  const parseValue = (value) => {
    let parsedValue = setVars(value);

    return base64 ? btoa(parsedValue) : parsedValue;
  };

  const resolveValue = (value) => {
    let resolvedValue = base64 ? atob(value) : value;

    return unsetVars(resolvedValue);
  };

  const handleUpdate = (value) => {
    const html = parseValue(value);

    field.onChange(html);
    field.onBlur();
  };

  return (
    <Labeled
      isRequired={required}
      label={label}
      id={`${id}-label`}
      color={fieldState?.invalid ? "error" : undefined}
      source={source}
      resource={resource}
      fullWidth={fullWidth}
      sx={{
        ".RaLabeled-label": {
          marginLeft: "14px",
        },
      }}
    >
      <Root className={className}>
        <Editor
          onInit={(evt, editor) => (editorRef.current = editor)}
          initialValue={resolveValue(value)}
          disabled={disabled || readOnly}
          onEditorChange={handleUpdate}
          init={{
            height: 500,
            menubar: false,
            plugins: [
              "advlist autolink lists link charmap print preview anchor",
              "searchreplace visualblocks code fullscreen",
              "insertdatetime media table paste code help wordcount",
              "noneditable"
            ],
            toolbar:
              "undo redo | formatselect fontselect | " +
              "bold italic backcolor | alignleft aligncenter " +
              "alignright alignjustify | bullist numlist outdent indent | " +
              "removeformat | help | link variable image | component | code",
            content_style:
              "body { font-family:Helvetica,Arial,sans-serif; font-size:14px } " +
              "sc { background-color: #1976d2; color: white; padding: 5px 15px; cursor: pointer; margin: 3px; border-radius: 3px; font-weight: bold; font-size: 18px;}",
            convert_urls: false,
            allow_html_in_named_anchor: true,
            allow_unsafe_link_target: true,
            allow_conditional_comments: true,
            custom_elements: "sc",
            extended_valid_elements: "sc[*]",
            noneditable_noneditable_class: "nonedit",
            setup: (editor) => {
              editor.ui.registry.addButton("image", {
                icon: "image",
                tooltip: "Insert Image",
                onAction: function (api): void {
                  if (imageContentInputRef.current) {
                    imageContentInputRef.current.open();
                  }
                },
              });
              editor.ui.registry.addButton("component", {
                icon: "code-sample",
                tooltip: "Insert Component",
                onAction: function (api): void {
                  setSelectedElement(null);
                  if (componentInputRef.current) {
                    componentInputRef.current.open();
                  }
                },
              });

              const isComponentElement = (node) => {
                return node.nodeName.toLowerCase() === "sc";
              };

              const getComponentElement = () => {
                const node = editor.selection.getNode();
                return isComponentElement(node) ? node : null;
              };

              editor.ui.registry.addContextForm("paragraph-form", {
                launch: {
                  type: "contextformtogglebutton",
                  icon: "edit",
                },
                initValue: () => {
                  const elm = getComponentElement();
                  return !!elm ? elm.id : "";
                },
                label: "Edit Component",
                predicate: isComponentElement,
                commands: [
                  {
                    type: "contextformtogglebutton",
                    icon: "edit-block",
                    tooltip: "Edit",

                    primary: true,
                    onSetup: (buttonApi) => {
                      buttonApi.setActive(!!getComponentElement());
                      const nodeChangeHandler = () => {
                        buttonApi.setActive(
                          !editor.readonly && !!getComponentElement()
                        );
                      };
                      editor.on("nodechange", nodeChangeHandler);
                      setSelectedElement(getComponentElement());
                      return () => editor.off("nodechange", nodeChangeHandler);
                    },
                    onAction: (formApi) => {
                      setSelectedElement(getComponentElement());
                      if (componentEditRef.current) {
                        componentEditRef.current.open();
                      }
                      formApi.hide();
                    },
                  },
                ],
              });

              editor.ui.registry.addMenuButton("variable", {
                icon: "visualblocks",
                fetch: function (callback) {
                  const items: any = [
                    {
                      type: "menuitem",
                      text: "baseUrl",
                      onAction: function () {
                        editor.insertContent("%baseUrl%");
                      },
                    },
                    {
                      type: "menuitem",
                      text: "cloudFrontUrl",
                      onAction: function () {
                        editor.insertContent("%cloudFrontUrl%");
                      },
                    },
                  ];
                  callback(items);
                },
              });
              editor.on('keydown', function (event) {
                if (event.keyCode === 8 || event.keyCode === 46)  {
                  var container = editor.selection.getStart();

                  if (container && container.tagName == 'SC') {
                    event.preventDefault();
                    event.stopPropagation()
                    return false;
                  }
              
                  return true;
                }
              });
            },
          }}
          tinymceScriptSrc="/js/tinymce/tinymce.min.js"
        />

        <ImageContentInput
          editor={editorRef}
          ref={imageContentInputRef}
        ></ImageContentInput>

        <ComponentInput
          editor={editorRef}
          setElement={setSelectedElement}
          ref={componentInputRef}
        ></ComponentInput>

        <ComponentInput
          element={selectedElement}
          setElement={setSelectedElement}
          editor={editorRef}
          ref={componentEditRef}
        ></ComponentInput>

        <FormHelperText
          sx={{width: "100%", marginTop: "14px", paddingLeft: "14px"}}
          className={
            (isTouched || isSubmitted) && invalid
              ? "tinymce-content-input-error"
              : ""
          }
          error={(isTouched || isSubmitted) && invalid}
        >
          <InputHelperText
            touched={isTouched || isSubmitted}
            error={error?.message}
            helperText={helperText}
          />
        </FormHelperText>
      </Root>
    </Labeled>
  );
};

const PREFIX = "TinyMCEContentInput";
const classes = {
  editorContent: `${PREFIX}-editorContent`,
};
const Root = styled("div", {
  name: PREFIX,
  overridesResolver: (props, styles) => styles.root,
})(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  [`& .tox-tinymce`]: {
    width: "100%",
    "& .tox-statusbar": {
      display: "none",
    },
    "&--disabled": {
      "&:before": {
        backgroundColor: theme.palette.action.disabledBackground,
        content: '""',
        width: "100%",
        height: "100%",
        position: "absolute",
        zIndex: 1,
      },
    },
  },
}));

TinyMCEContentInput.defaultProps = {
  base64: false,
  disabled: false,
  required: false,
  readOnly: false,
  fullWidth: false,
  defaultValue: "",
};

export type TinyMCEContentInputProps = CommonInputProps & {
  disabled?: boolean;
  readOnly?: boolean;
  base64?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  helperText?: string | React.ReactElement | false;
  className?: string;
  source: string;
};
